export type RequiredGeneratedChangeType =
  // If we are changing an export, we are adding back removed exports and changing the bundle.
  | 'changed-export'
  // If we are adding an export, we are changing the bundle.
  | 'added-export'
  // If we are adding a dynamic import, we are changing the bundle.
  | 'added-dynamic-import'
  // If we are adding an included dependency and we are changing resolution, we are changing the resolve map.
  | 'included-dependency-required'
  // If we are removing an excluded dependency and adding it back to the resolve map, we are changing the resolve map.
  | 'removed-excluded-dependency-required';

export type GeneratedChangeType =
  | RequiredGeneratedChangeType
  // Included dependencies are optional if they are already in the import map as including it won't affect resolution.
  | 'included-dependency-optional'
  // Excluded dependencies are optional since they excluding them only helps to make package resolution faster.
  | 'excluded-dependency'
  // Removing an excluded dependency can be optional if it won't add the package back to the resolve map.
  | 'removed-excluded-dependency-optional';

export interface GeneratedChange {
  type: GeneratedChangeType;
  /** Item that changed, such as the dependency name, export path, or bundler type */
  change: string;
  /** Additional explanation */
  reason?: string;
}

/**
 * Mapping of required change types to true.
 * (Keys for non-required change types will be undefined.)
 */
// It's easier to ensure this stays up to date by using an object rather than an array.
// Any GeneratedChangeType is allowed as a lookup key to make usage easier.
export const requiredChangeTypes: Partial<Record<GeneratedChangeType, true>> = {
  'changed-export': true,
  'added-export': true,
  'added-dynamic-import': true,
  'included-dependency-required': true,
  'removed-excluded-dependency-required': true,
} satisfies Record<RequiredGeneratedChangeType, true>;

/**
 * Mapping of optional change types to true.
 * (Keys for required change types will be undefined.)
 */
export const optionalChangeTypes: Partial<Record<GeneratedChangeType, true>> = {
  'included-dependency-optional': true,
  'excluded-dependency': true,
  'removed-excluded-dependency-optional': true,
} satisfies Record<Exclude<GeneratedChangeType, RequiredGeneratedChangeType>, true>;
